import { useEffect, useRef } from 'react';
import { EventNames } from '../../common/constants/constants';
import { IEventTracker } from '../../common/hooks/useTrackEvent';

/**
 * Keeps the screen awake while an upload is moving bytes.
 *
 * A phone that dims and locks mid-upload backgrounds the tab, which pauses
 * timers and kills in-flight requests (the "stalled then left" failures).
 * The Screen Wake Lock API prevents that while `active` is true.
 *
 * - Browsers release the lock whenever the page is hidden, so it is
 *   re-requested each time the page becomes visible again while still active.
 * - Unsupported browsers (older iOS before 16.4, Firefox before 126) simply
 *   don't get it; the upload works exactly as before.
 * - A request can be refused (Low Power Mode, battery saver, permissions
 *   policy); that is reported and otherwise ignored.
 * - Safari grants the first request on a page only inside a tap (transient
 *   activation), then remembers that grant for the page's lifetime
 *   (WebKit's WakeLock::request; webkit.org/b/255363). The upload starts long
 *   after the Choose Video tap, so on iPhone the request here was refused and
 *   the phone auto-locked mid-upload. `primeScreenWakeLock` takes and drops a
 *   lock inside the first tap so every later request, including the one on
 *   return to the page, is allowed. While the upload runs, any tap also
 *   retries a lock that isn't held. Chrome needs none of this.
 * - A request that never settles would leave the hook pending forever and
 *   silent; after REQUEST_TIMEOUT_MS it is reported as `timeout` and the next
 *   chance (tap, return to the page) asks again.
 *
 * Telemetry: `wake_lock_result` with outcome acquired | reacquired | denied |
 * unsupported | timeout | released (+ held_ms and why), and primed | denied
 * with phase=prime for the first-tap grant.
 */
const REQUEST_TIMEOUT_MS = 5_000;

const isSupported = (): boolean => typeof navigator !== 'undefined' && 'wakeLock' in navigator;

let primedThisPage = false;

/**
 * Call from a tap handler (synchronously, before any await). Takes a screen
 * lock while the tap still counts as a user gesture and releases it at once;
 * Safari then allows later requests without a gesture. Once per page load.
 */
export const primeScreenWakeLock = (trackEvent: IEventTracker): void => {
  if (primedThisPage || !isSupported() || document.visibilityState !== 'visible') return;
  primedThisPage = true;
  navigator.wakeLock.request('screen').then(
    (sentinel) => {
      sentinel.release().catch(() => undefined);
      trackEvent({ action: EventNames.wake_lock_result, outcome: 'primed' });
    },
    (err) => {
      // Let the next tap try again.
      primedThisPage = false;
      trackEvent({
        action: EventNames.wake_lock_result,
        outcome: 'denied',
        phase: 'prime',
        error_code: (err as { name?: string } | null)?.name ?? 'Error',
      });
    },
  );
};

export const useUploadWakeLock = (active: boolean, trackEvent: IEventTracker): void => {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  const acquiredAtRef = useRef<number | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const trackRef = useRef(trackEvent);
  trackRef.current = trackEvent;
  const reportedUnsupportedRef = useRef(false);
  const pendingRef = useRef(false);
  const acquiredOnceRef = useRef(false);

  useEffect(() => {
    const supported = isSupported();

    const release = (reason: string) => {
      const sentinel = sentinelRef.current;
      if (!sentinel) return;
      sentinelRef.current = null;
      const heldMs = acquiredAtRef.current !== null ? Math.round(performance.now() - acquiredAtRef.current) : null;
      acquiredAtRef.current = null;
      sentinel.release().catch(() => undefined);
      trackRef.current({ action: EventNames.wake_lock_result, outcome: 'released', reason, held_ms: heldMs });
    };

    const acquire = async (isReacquire: boolean) => {
      if (!activeRef.current || sentinelRef.current || pendingRef.current) return;
      if (document.visibilityState !== 'visible') return;
      if (!supported) {
        if (!reportedUnsupportedRef.current) {
          reportedUnsupportedRef.current = true;
          trackRef.current({ action: EventNames.wake_lock_result, outcome: 'unsupported' });
        }
        return;
      }
      pendingRef.current = true;
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        pendingRef.current = false;
        trackRef.current({ action: EventNames.wake_lock_result, outcome: 'timeout', ms: REQUEST_TIMEOUT_MS });
      }, REQUEST_TIMEOUT_MS);
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        clearTimeout(timer);
        // Answered after the timeout, or the upload finished while pending.
        if (settled || !activeRef.current) {
          if (!settled) pendingRef.current = false;
          settled = true;
          sentinel.release().catch(() => undefined);
          return;
        }
        settled = true;
        pendingRef.current = false;
        sentinelRef.current = sentinel;
        acquiredAtRef.current = performance.now();
        acquiredOnceRef.current = true;
        // Fired when the browser drops it (page hidden, battery saver).
        sentinel.addEventListener('release', () => {
          if (sentinelRef.current !== sentinel) return; // released by us
          sentinelRef.current = null;
          const heldMs = acquiredAtRef.current !== null ? Math.round(performance.now() - acquiredAtRef.current) : null;
          acquiredAtRef.current = null;
          trackRef.current({
            action: EventNames.wake_lock_result,
            outcome: 'released',
            reason: document.visibilityState === 'visible' ? 'system' : 'hidden',
            held_ms: heldMs,
          });
        });
        trackRef.current({ action: EventNames.wake_lock_result, outcome: isReacquire ? 'reacquired' : 'acquired' });
      } catch (err) {
        clearTimeout(timer);
        if (settled) return;
        settled = true;
        pendingRef.current = false;
        trackRef.current({
          action: EventNames.wake_lock_result,
          outcome: 'denied',
          error_code: (err as { name?: string } | null)?.name ?? 'Error',
        });
      }
    };

    if (active) {
      acquire(false);
    } else {
      release('upload_stopped');
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && activeRef.current) acquire(true);
    };
    // A tap is a user gesture, which Safari requires if the page has no
    // earlier grant (e.g. the first-tap prime was refused or never ran).
    const onTap = () => {
      if (activeRef.current) acquire(acquiredOnceRef.current);
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('click', onTap, true);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('click', onTap, true);
    };
  }, [active]);

  // Leaving the screen releases it.
  useEffect(
    () => () => {
      const sentinel = sentinelRef.current;
      if (!sentinel) return;
      sentinelRef.current = null;
      sentinel.release().catch(() => undefined);
      const heldMs = acquiredAtRef.current !== null ? Math.round(performance.now() - acquiredAtRef.current) : null;
      trackRef.current({ action: EventNames.wake_lock_result, outcome: 'released', reason: 'unmount', held_ms: heldMs });
    },
    [],
  );
};
